import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'opticvance_cookie_consent';

const defaultPrefs = {
  essential: true,      // always on
  analytics: false,
  marketing: false,
};

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);

  /* Show banner only if consent has not been recorded yet */
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  const save = (consent) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...consent, timestamp: Date.now() }));
    } catch { /* storage unavailable */ }
    setVisible(false);
  };

  const acceptAll = () => save({ essential: true, analytics: true, marketing: true });
  const rejectAll = () => save({ essential: true, analytics: false, marketing: false });
  const savePrefs = () => save(prefs);

  const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="cookie-banner"
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 28 }}
          className="fixed bottom-0 left-0 right-0 z-[9999] px-4 pb-4 pointer-events-none"
          role="dialog"
          aria-modal="true"
          aria-label="Cookie consent"
        >
          <div className="pointer-events-auto max-w-4xl mx-auto rounded-2xl border border-white/10 bg-[#0d0d12]/90 backdrop-blur-xl shadow-2xl overflow-hidden">

            {/* Gradient top accent */}
            <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-primary to-transparent opacity-70" />

            <div className="p-5 sm:p-6">
              {!showPrefs ? (
                /* ── Main banner ── */
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  {/* Icon */}
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary/15 border border-primary/20 flex items-center justify-center">
                    <svg className="w-5 h-5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M8 9h.01M12 9h.01M16 9h.01M8 14h.01M12 14h.01M16 14h.01" />
                    </svg>
                  </div>

                  {/* Text */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm mb-1">We value your privacy</p>
                    <p className="text-gray-400 text-xs leading-relaxed">
                      We use cookies to enhance your browsing experience, analyse site traffic, and personalise content. By clicking&nbsp;
                      <span className="text-white font-medium">Accept All</span>, you consent to our use of cookies. You can&nbsp;
                      <button
                        onClick={() => setShowPrefs(true)}
                        className="text-primary hover:underline focus:outline-none"
                      >
                        manage your preferences
                      </button>
                      &nbsp;or learn more in our&nbsp;
                      <Link to="/cookie-policy" className="text-primary hover:underline" onClick={() => setVisible(false)}>
                        Cookie Policy
                      </Link>.
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 sm:flex-shrink-0">
                    <button
                      onClick={rejectAll}
                      className="px-4 py-2 rounded-lg text-xs font-medium text-gray-300 border border-white/10 bg-white/5 hover:bg-white/10 transition-colors whitespace-nowrap"
                    >
                      Reject Non-Essential
                    </button>
                    <button
                      onClick={() => setShowPrefs(true)}
                      className="px-4 py-2 rounded-lg text-xs font-medium text-gray-300 border border-white/10 bg-white/5 hover:bg-white/10 transition-colors whitespace-nowrap"
                    >
                      Manage Preferences
                    </button>
                    <button
                      onClick={acceptAll}
                      className="px-4 py-2 rounded-lg text-xs font-semibold text-white bg-primary hover:bg-primary/90 transition-colors whitespace-nowrap"
                    >
                      Accept All
                    </button>
                  </div>
                </div>
              ) : (
                /* ── Preferences panel ── */
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-semibold text-sm">Cookie Preferences</h3>
                    <button
                      onClick={() => setShowPrefs(false)}
                      className="text-gray-500 hover:text-gray-300 transition-colors"
                      aria-label="Close preferences"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  </div>

                  <div className="space-y-3 mb-5">
                    <PreferenceRow
                      label="Essential Cookies"
                      description="Required for the site to function. Cannot be disabled."
                      checked={true}
                      disabled
                    />
                    <PreferenceRow
                      label="Analytics & Performance"
                      description="Help us understand how visitors interact with our site."
                      checked={prefs.analytics}
                      onChange={() => toggle('analytics')}
                    />
                    <PreferenceRow
                      label="Marketing & Personalisation"
                      description="Used to deliver relevant adverts and track campaign performance."
                      checked={prefs.marketing}
                      onChange={() => toggle('marketing')}
                    />
                  </div>

                  <div className="flex flex-wrap gap-2 justify-end">
                    <button
                      onClick={rejectAll}
                      className="px-4 py-2 rounded-lg text-xs font-medium text-gray-300 border border-white/10 bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      Reject All
                    </button>
                    <button
                      onClick={savePrefs}
                      className="px-4 py-2 rounded-lg text-xs font-semibold text-white bg-primary hover:bg-primary/90 transition-colors"
                    >
                      Save My Preferences
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ── Toggle row ── */
const PreferenceRow = ({ label, description, checked, disabled, onChange }) => (
  <div className="flex items-start justify-between gap-4 p-3 rounded-xl bg-white/[0.03] border border-white/[0.07]">
    <div className="min-w-0">
      <p className="text-white text-xs font-medium">{label}</p>
      <p className="text-gray-500 text-[11px] mt-0.5 leading-relaxed">{description}</p>
    </div>
    <button
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onChange}
      className={`relative flex-shrink-0 mt-0.5 w-9 h-5 rounded-full transition-colors duration-200 focus:outline-none ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      } ${checked ? 'bg-primary' : 'bg-white/10'}`}
    >
      <span
        className={`block w-3.5 h-3.5 rounded-full bg-white shadow-md absolute top-[3px] transition-transform duration-200 ${
          checked ? 'translate-x-[18px]' : 'translate-x-[3px]'
        }`}
      />
    </button>
  </div>
);

export default CookieBanner;

/**
 * Utility: read stored consent from anywhere in the app.
 * Returns null if the user has not consented yet.
 */
export const getCookieConsent = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};
